"use client"

import dynamic from "next/dynamic"
import React, { useRef, useEffect, useState } from "react"
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion"
import { useLanguage } from "@/hooks/use-language"

const SentientSphere = dynamic(() => import("./sentient-sphere").then(mod => mod.SentientSphere), { ssr: false })
const PortfolioInterior = dynamic(() => import("./portfolio-interior").then(mod => mod.PortfolioInterior), { ssr: false })

interface HeroProps {
  onAiContextClick?: () => void
  onContactClick?: () => void
  isExploring: boolean
  showInterior: boolean
  onExplore: () => void
  onExit: () => void
  onReady?: () => void
}

export function Hero({ onAiContextClick, onContactClick, isExploring, showInterior, onExplore, onExit, onReady }: HeroProps) {
  const { t } = useLanguage()
  const containerRef = useRef<HTMLElement>(null)
  const [loadSphere, setLoadSphere] = useState(false)

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  })

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8])
  const bgDarken = useTransform(scrollYProgress, [0, 0.8], [0, 1])
  const scrollOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0])

  const [sphereLoaded, setSphereLoaded] = useState(false)

  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === "#portfolio") {
        onExplore()
      }
    }
    openFromHash()
    window.addEventListener("hashchange", openFromHash)
    return () => window.removeEventListener("hashchange", openFromHash)
  }, [onExplore])

  useEffect(() => {
    let cancelled = false
    const start = () => {
      // Use requestIdleCallback for ultra-low impact hydration
      const init = () => {
        if (!cancelled) setLoadSphere(true)
      }
      
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => init(), { timeout: 2000 })
      } else {
        window.setTimeout(init, 800)
      }
    }

    start()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <>
      <section ref={containerRef} className="relative h-screen w-full overflow-hidden bg-transparent">
        {/* LCP HACK: The Ghost Sphere Placeholder */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <div 
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] md:w-[40vw] md:h-[40vw] rounded-full transition-opacity duration-1000 ease-in-out ${sphereLoaded ? 'opacity-0' : 'opacity-25'}`}
            style={{
              background: 'radial-gradient(circle, rgba(16,185,129,0.3) 0%, rgba(5,20,40,0.1) 60%, transparent 100%)',
              filter: 'blur(60px)',
              mixBlendMode: 'screen',
            }}
          />
        </div>

        <div className="absolute inset-0">
          {loadSphere && (
            <div className={`w-full h-full transition-opacity duration-1000 ${sphereLoaded ? 'opacity-100' : 'opacity-0'}`}>
              <SentientSphere 
                isExploring={isExploring} 
                scrollYProgress={scrollYProgress} 
                onCreated={() => {
                  setSphereLoaded(true);
                  if (onReady) onReady();
                }}
              />
            </div>
          )}
        </div>

        <motion.div
          style={{ opacity: bgDarken }}
          className="absolute inset-0 z-20 bg-transparent pointer-events-none"
        />

        <motion.div 
          style={{ opacity, scale }} 
          className="relative z-10 h-full flex flex-col items-center justify-start md:justify-center pt-32 md:pt-12 p-8 md:p-12 pointer-events-none"
        >
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="absolute top-12 right-12 z-30 pointer-events-auto hidden md:flex items-center gap-3 px-4 py-2 rounded-full border border-emerald-500/10 bg-emerald-500/5 backdrop-blur-md"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse" />
            <span className="font-mono text-[8px] tracking-[0.3em] text-emerald-400 uppercase">
              {t.hero.available}
            </span>
          </motion.div>

          <motion.div
            transition={{
              duration: isExploring ? 0.24 : 0.4,
              ease: isExploring ? [0.32, 0.72, 0, 1] : [0.23, 1, 0.32, 1],
            }}
            animate={
              isExploring
                ? { opacity: 0, y: -18, scale: 0.97, filter: "blur(10px)" }
                : { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
            }
            className="flex flex-col items-center gap-8 md:gap-12"
          >
            <div
              className="text-center pointer-events-auto flex flex-col items-center"
            >
              <p className="font-mono text-[9px] tracking-[0.5em] text-emerald-500/60 mb-6 uppercase">{t.hero.tagline}</p>

              <h1 className="font-playfair italic text-5xl md:text-7xl lg:text-8xl font-light tracking-tight text-balance mb-8 text-white">
                Diego Nuñez
                <br />
                Necochea
              </h1>

              <div className="max-w-2xl mx-auto mb-10 md:mb-12">
                <p className="font-sans text-lg md:text-xl font-light text-white/60 leading-tight text-balance px-4 md:px-0">
                  {t.hero.description.split(".").map((sentence, i) => {
                    if (sentence.trim() === "") return null
                    return (
                      <React.Fragment key={i}>
                        {sentence.split(/(\[.*?\]\(.*?\))/g).map((part, j) => {
                          const match = part.match(/\[(.*?)\]\((.*?)\)/)
                          if (match) {
                            const [_, text, style] = match
                            return (
                              <span
                                key={j}
                                className={style === "italic" ? "text-white italic" : "text-white"}
                              >
                                {text}
                              </span>
                            )
                          }
                          return part
                        })}
                        {i === 0 ? "." : ""}
                        {i === 0 && <br className="hidden md:block" />}
                      </React.Fragment>
                    )
                  })}
                </p>
              </div>

              <div className="flex flex-col items-center gap-10">
                <motion.button
                  data-cursor-hover
                  onClick={onExplore}
                  whileHover={isExploring ? {} : { scale: 1.02 }}
                  whileTap={isExploring ? {} : { scale: 0.98 }}
                  className="relative px-12 py-5 border border-white/10 rounded-full font-mono text-xs tracking-[0.3em] uppercase bg-white/[0.05] backdrop-blur-2xl text-white hover:bg-white hover:text-black transition-all duration-500 shadow-2xl"
                >
                  {t.hero.viewProjects}
                </motion.button>

                <motion.a
                  href={t.hero.cvPath}
                  download
                  whileHover={{ y: -2 }}
                  className="group flex flex-col items-center gap-3 transition-all duration-300"
                >
                  <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/40 group-hover:text-emerald-400 transition-colors duration-500">
                    {t.hero.downloadCv}
                  </span>
                  <div className="w-12 h-[1px] bg-white/10 group-hover:w-20 group-hover:bg-emerald-500/50 transition-all duration-700 ease-out" />
                </motion.a>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ opacity: scrollOpacity }}
          initial={{ opacity: 0 }}
          animate={isExploring ? { opacity: 0, y: 12 } : { opacity: 1, y: 0 }}
          transition={isExploring ? { duration: 0.18 } : { delay: 1.5 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 pointer-events-auto cursor-pointer group/scroll"
          onClick={() => {
            document.querySelector("#about")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            })
          }}
        >
          <div className="flex flex-col items-center">
            <span className="font-mono text-[9px] tracking-[0.5em] text-white/40 group-hover/scroll:text-white transition-colors uppercase mb-6 animate-pulse">
              Scroll
            </span>
            <div className="relative w-[2px] h-24 bg-white/5 overflow-hidden">
              <motion.div
                className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-white/40 to-transparent group-hover/scroll:via-white transition-colors"
                animate={{
                  y: ["-100%", "100%"],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
            </div>
          </div>
        </motion.div>
      </section>

      <AnimatePresence>
        {showInterior && (
          <PortfolioInterior onContactClick={onContactClick} onExit={onExit} />
        )}
      </AnimatePresence>
    </>
  )
}
